// ============================================
// Notifications Hooks - TanStack Query
// ============================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type { UUID } from '@conai/shared-types';
import { useNotificationsService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';

/**
 * Hook to list notifications.
 *
 * `data` là `{ items, count }` chứ không phải mảng: `count` là TỔNG SỐ thật
 * (`meta.count`), còn `items` bị endpoint cắt cứng 100 bản ghi mới nhất và chưa
 * phân trang — không có `count` thì không chỗ nào biết còn bao nhiêu cái nữa.
 */
export function useNotifications(params?: { page?: number; page_size?: number }) {
  const service = useNotificationsService();

  return useQuery({
    queryKey: ['notifications', 'list', params],
    queryFn: () => service.listWithMeta(params),
  });
}

/**
 * Hook to mark a notification as read
 */
export function useMarkNotificationAsRead() {
  const service = useNotificationsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => service.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/**
 * Hook to delete a notification
 */
export function useDeleteNotification() {
  const service = useNotificationsService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => service.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Notification deleted');
    },
  });
}
